import { isEmpty } from "@swiggy-private/js-utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { NEWRELIC_TABLES, recordNREvent, isMROFlow } from "@swiggy-private/checkout-core";
import { isValidNumber } from "@swiggy-private/core-ui";

type TickerTimer = {
    enabled: boolean;
    minutes?: number;
    seconds?: number;
};

const NR_EVENT_TYPE = {
    UPDATED_TS_RECEIVED: "updated_ts_received",
};

export const useIPLSwiggySixesTickerTimer = (
    timestampInMs?: number,
    setCroutonTrackingInSession?: (value: boolean) => void,
) => {
    const timerRef = useRef<NodeJS.Timer>();
    const [tickerTimerInMinSec, setTickerTimerInMinSec] = useState<TickerTimer>({ enabled: false });
    // Keep enabled in a ref so the invalid-timestamp path can read it without putting
    // tickerTimerInMinSec.enabled in the effect deps (that re-ran the effect after an
    // explicit disable and immediately re-enabled the ticker while timestamp was still valid).
    const tickerEnabledRef = useRef(false);
    tickerEnabledRef.current = tickerTimerInMinSec.enabled;

    const cleanupTickerTimer = useCallback(() => {
        if (!isEmpty(timerRef.current)) {
            clearInterval(timerRef.current as NodeJS.Timeout);
        }
    }, []);

    const disableTickerTimerAndComms = useCallback(() => {
        setTickerTimerInMinSec({ enabled: false });
        cleanupTickerTimer();
    }, [cleanupTickerTimer]);

    const calculateTickerInStore = useCallback(() => {
        if (isEmpty(timestampInMs) || !isValidNumber(timestampInMs)) {
            disableTickerTimerAndComms();
            return;
        }

        const now = Date.now();
        const diff = timestampInMs - now;
        // If time has already passed, return 0
        if (diff <= 0) {
            cleanupTickerTimer();
            setTickerTimerInMinSec({ enabled: true, minutes: 0, seconds: 0 });
            return;
        }
        // get number of minutes
        // 1 min = 60000 ms
        const minutes = Math.floor(diff / 60000);
        // get number of seconds
        // 1 sec = 1000 ms
        const seconds = Math.floor((diff % 60000) / 1000);
        setTickerTimerInMinSec({ enabled: true, minutes, seconds });
        // Non-MRO (BAU): mark the session so hide/close can restore the crouton across a reload.
        // MRO: the session flag is never read - visibility is driven by enabledCroutonId and the
        // crouton list priority - so skip the write and keep the flag free of MRO state.
        if (!isMROFlow()) {
            setCroutonTrackingInSession?.(true);
        }
    }, [cleanupTickerTimer, disableTickerTimerAndComms, timestampInMs, setCroutonTrackingInSession]);

    useEffect(() => {
        // No valid timestamp: disable ticker (clears interval) and skip scheduling.
        if (isEmpty(timestampInMs) || !isValidNumber(timestampInMs)) {
            if (tickerEnabledRef.current) {
                disableTickerTimerAndComms();
            } else {
                cleanupTickerTimer();
            }
            return cleanupTickerTimer;
        }

        timerRef.current = setInterval(calculateTickerInStore, 1000);
        calculateTickerInStore();
        return cleanupTickerTimer;
    }, [calculateTickerInStore, cleanupTickerTimer, disableTickerTimerAndComms, timestampInMs]);

    useEffect(() => {
        if (!isEmpty(timestampInMs) && isValidNumber(timestampInMs)) {
            recordNREvent({
                eventType: NEWRELIC_TABLES.IPL_SWIGGY_SIXES_DEBUG,
                type: NR_EVENT_TYPE.UPDATED_TS_RECEIVED,
                timestampInMsec: String(timestampInMs || ""),
                clientTimeInSec: String(Math.ceil(Date.now() / 1000)),
            });
        }
    }, [timestampInMs]);

    return {
        tickerTimerInMinSec,
        cleanupTickerTimer,
        disableTickerTimerAndComms,
    };
};
